/**
 * ProCalculatorResults Component
 *
 * Results display panel with lazy-loaded ProResultsTable, save section, and export section
 * Extracted from ProCalculator.tsx (lines 1548-1582)
 */

import { lazy, Suspense } from 'react';
import { Paper, Title, Center, Loader, Divider } from '@mantine/core';
import {
  SaveProCalculationSection,
  ExportProCalculationSection,
} from './ProCalculator';
import type { ProCalculatorFormValues, ProCalculatorResults as ProCalculatorResultsType, TariffType } from '../../../types/proCalculator';

// Performance: Lazy load heavy results table component
const ProResultsTable = lazy(() =>
  import('./ProCalculator/ProResultsTable')
    .then(m => ({ default: m.ProResultsTable }))
);

interface ProCalculatorResultsProps {
  results: ProCalculatorResultsType;
  formValues: ProCalculatorFormValues;
  baselineTariff: TariffType;
  isSaving: boolean;
  saveError?: string;
  saveSuccess: boolean;
  calculationExists: boolean;
  loading: boolean;
  onBaselineTariffChange: (tariff: TariffType) => void;
  onSaveProCalculation: () => void;
}

export function ProCalculatorResults({
  results,
  formValues,
  baselineTariff,
  isSaving,
  saveError,
  saveSuccess,
  calculationExists,
  loading,
  onBaselineTariffChange,
  onSaveProCalculation,
}: ProCalculatorResultsProps) {
  if (loading) {
    return null;
  }

  return (
    <>
      {/* Results Table - Lazy loaded for performance */}
      <Paper p="xl" shadow="sm" withBorder>
        <Title order={2} mb="lg">
          Wyniki porównania taryf
        </Title>
        <Suspense fallback={<Center py="xl"><Loader size="lg" /></Center>}>
          <ProResultsTable
            results={results}
            baselineTariff={baselineTariff}
            onBaselineTariffChange={onBaselineTariffChange}
          />
        </Suspense>
      </Paper>

      <Divider my="xl" />

      {/* Save Calculation Section */}
      <SaveProCalculationSection
        formValues={formValues}
        results={results}
        onSave={onSaveProCalculation}
        isSaving={isSaving}
        saveError={saveError}
        saveSuccess={saveSuccess}
        calculationExists={calculationExists}
      />

      {/* Export Section - Show for authenticated users with results */}
      <ExportProCalculationSection
        formValues={formValues}
        results={results}
      />
    </>
  );
}
